/**
 * PipelineBoard — Kanban pipeline view, one column per pipeline stage.
 * Blueprint: Section D — <PipelineBoard> modifications, F-10 dynamic stages.
 *
 * Columns sourced from: GET /admin/pipeline-stages/:tort_code ordered by sort_order.
 * Claimants fetched per-stage via: GET /claimants?tort_code=&pipeline_stage=.
 *
 * TODO: Integrate react-beautiful-dnd or @dnd-kit/core for drag-to-move-stage.
 * TODO: Stage column header shows active claimant count badge.
 * TODO: SUBSTANTIATED / AWARD_READY columns show "Generate Packet" floating action.
 * TODO: Filter bar (tort selector, search, deficiency/overlap toggles) at top.
 * TODO: CLAIMS_ADMIN sees all stages; CLIENT_USER cannot drag backward (F-04/F-10).
 */
import React, { useEffect, useState } from 'react';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { Plus, Filter } from 'lucide-react';
import { useClaimants } from '@/hooks/cmm/useClaimants';
import { usePipelineStages } from '@/hooks/cmm/usePipelineStages';
import { ClaimantCard } from '@/components/cmm/ClaimantCard';
import type { TortCode, Claimant } from '@/types/cmm/claimant.types';

const BREADCRUMBS = [
  { label: 'Claimants', href: '/claimants' },
  { label: 'Pipeline View' },
];

const PipelineBoard: React.FC = () => {
  const [activeTort, setActiveTort] = useState<TortCode>('RLP');
  const [selected,   setSelected]   = useState<Claimant | null>(null);

  const { stages, fetchStages }           = usePipelineStages();
  const { claimants, fetchClaimants }     = useClaimants(activeTort);

  useEffect(() => { fetchStages(activeTort); fetchClaimants(); }, [activeTort]);

  const claimantsByStage = (stageKey: string) =>
    claimants.filter(c => c.pipelineStage === stageKey);

  const headerActions = (
    <div className="flex items-center gap-2">
      <select value={activeTort}
        onChange={e => setActiveTort(e.target.value as TortCode)}
        className="text-sm border border-gray-300 rounded-md px-3 py-1.5">
        {(['SMA','VGA','RLP','PFAS'] as TortCode[]).map(t => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      <button className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50">
        <Filter className="w-4 h-4" /> Filter
      </button>
      <button className="flex items-center gap-1.5 text-sm text-white bg-blue-600 rounded-md px-3 py-1.5 hover:bg-blue-700">
        <Plus className="w-4 h-4" /> Add Claimant
      </button>
    </div>
  );

  return (
    <BaseLayout
      title="Pipeline View"
      subtitle={`${activeTort} claimants by pipeline stage`}
      breadcrumbs={BREADCRUMBS}
      headerActions={headerActions}
    >
      {stages.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
          Loading pipeline stages...
          {/* TODO: Remove — replace with real data from usePipelineStages */}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '60vh' }}>
          {stages.map(stage => {
            const cards = claimantsByStage(stage.stageKey);
            return (
              <div key={stage.id} className="flex-shrink-0 w-72 bg-gray-50 rounded-xl border border-gray-200">
                {/* Column Header */}
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{stage.stageLabel}</p>
                    <p className="text-xs text-gray-400">{stage.activeClaimantCount} claimants</p>
                  </div>
                  {stage.isTerminal && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Terminal</span>
                  )}
                  {stage.allowsPacketGeneration && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Packet ✓</span>
                  )}
                </div>

                {/* Cards */}
                <div className="p-2 space-y-2 overflow-y-auto" style={{ maxHeight: '65vh' }}>
                  {cards.length === 0 ? (
                    <p className="text-xs text-center text-gray-300 py-6">No claimants</p>
                  ) : (
                    cards.map(c => (
                      <ClaimantCard key={c.id} claimant={c} onClick={setSelected} />
                    ))
                  )}
                  {/* TODO: Drop zone for drag-and-drop here */}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* TODO: <ClaimantDetailPanel> slide-over when selected !== null */}
      {selected && (
        <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 z-40 p-4">
          <p className="text-sm font-semibold">Detail Panel — TODO</p>
          <p className="text-xs text-gray-500">{selected.firstName} {selected.lastName}</p>
          <button onClick={() => setSelected(null)} className="mt-2 text-xs text-blue-600">Close</button>
        </div>
      )}
    </BaseLayout>
  );
};

export default PipelineBoard;
